import { subMonths } from 'date-fns'
import { ComponentPropsWithoutRef, ReactNode, type JSX } from 'react';
import Markdown from 'react-markdown'
import { LinkButtonReactRouter } from '~/components/button'
import { DevOnly } from '~/components/role-only'
import { Monotype } from '~/components/text'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Copy,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TooltipContent,
} from '@hap/components'
import { ScorecardScale } from '@hap/constants'
import { toPercent } from '@hap/utils'

import { scaleOptions } from '../criteria-forms/constants'

export function CriteriaTooltip(props: Props): JSX.Element {
  // Use scorecard-specific definition when available, fallback to default
  const displayDefinition = props.assignment?.definition || props.definition

  return (
    <TooltipContent
      className="w-[400px] p-0"
      align={props.align}
      side={props.side}
      sideOffset={10}
    >
      <header className="flex items-center justify-between gap-2 border-b p-4 py-2">
        <h4 className="flex items-center gap-2 font-semibold">
          {props.name}
          {props.auto && props.autoScoreCriteria && props.updatedBy === null && (
            <Badge variant="success">Auto</Badge>
          )}
          {props.autoFail && <Badge variant="warn">Auto Fail</Badge>}
          {props.isSingleScorecardFiltered && props.assignment?.definition && (
            <Badge variant="secondary">Scorecard Specific</Badge>
          )}
        </h4>
        {props.actions?.map((action, i) => (
          <LinkButtonReactRouter variant="outline" to={action.to} key={i} size="sm">
            {action.label}
          </LinkButtonReactRouter>
        ))}
      </header>

      <div className="space-y-4 p-4">
        <div>
          <p className="text-accent-foreground">Definition</p>
          <div className="max-h-[300px] overflow-y-auto">
            <Markdown
              components={{
                p: ({ children }) => <span className="mb-4 block">{children}</span>,
                a: ({ children, href }) => (
                  <a
                    className="text-link-primary relative"
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {children}
                    <span className="bg-link-primary/15 absolute left-0 top-1/2 h-3 w-full select-none" />
                  </a>
                ),
                ul: ({ children }) => <ul className="mb-6 ml-6 list-disc">{children}</ul>,
                ol: ({ children }) => <ol className="mb-6 ml-6 list-decimal">{children}</ol>,
                li: ({ children }) => <li>{children}</li>,
                h1: ({ children }) => <h1 className="mb-4 font-bold">{children}</h1>,
                h2: ({ children }) => <h2 className="mb-4 font-bold">{children}</h2>,
                h3: ({ children }) => <h3 className="mb-4 font-bold">{children}</h3>,
              }}
            >
              {displayDefinition}
            </Markdown>
          </div>
        </div>

        <div>
          <p className="text-accent-foreground">Scale</p>
          <p className="group" data-selection={props.awardedPoints}>
            {scaleOptions.find((s) => s.value === props.scale)?.label}
          </p>
        </div>

        {props.notes && (
          <div>
            <p className="text-accent-foreground">Notes</p>
            <p>{props.notes}</p>
          </div>
        )}
      </div>

      {props.score !== undefined && props.userId && props.criteriaId && (
        <CriteriaTooltipTrends
          userId={props.userId}
          criteriaId={props.criteriaId}
          score={props.score}
        />
      )}

      <DevOnly>
        <Accordion type="multiple" className="p-4 pt-0">
          <AccordionItem value="debug" className="border-b-0">
            <AccordionTrigger>Debug</AccordionTrigger>
            <AccordionContent>
              <Table className="border">
                <TableBody>
                  <TableRow>
                    <TableCell>Criteria ID</TableCell>
                    <TableCell>
                      {props.criteriaId ? (
                        <Monotype>
                          <Copy value={props.criteriaId} />
                        </Monotype>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Criteria Assignment ID</TableCell>
                    <TableCell>
                      {props.criteriaAssignmentId ? (
                        <Monotype>
                          <Copy value={props.criteriaAssignmentId} />
                        </Monotype>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Evaluation ID</TableCell>
                    <TableCell>
                      {props.evaluationId ? (
                        <Monotype>
                          <Copy value={props.evaluationId} />
                        </Monotype>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Evaluation Score ID</TableCell>
                    <TableCell>
                      {props.evaluationScoreId ? (
                        <Monotype>
                          <Copy value={props.evaluationScoreId} />
                        </Monotype>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </DevOnly>
    </TooltipContent>
  )
}

const TODAY = new Date()

function CriteriaTooltipTrends(props: {
  userId: string
  criteriaId: string
  score: Nullable<number>
}): JSX.Element {
  const { data } = useAnalytics({
    aggregations: {
      orgAvg: {
        op: 'avg',
        field: 'score',
        dataset: 'evaluationCriteriaScores',
      },
      agentAvg: {
        op: 'avg',
        field: 'score',
        dataset: 'evaluationCriteriaScores',
        filter: [
          { field: 'handledBy', op: 'eq', value: props.userId, dataset: 'interactions' },
        ],
      },
    },
    filters: [
      {
        dataset: 'evaluationCriteriaScores',
        field: 'scorecardCriteriaId',
        op: 'eq',
        value: props.criteriaId,
      },
    ],
    timespan: {
      start: subMonths(TODAY, 3),
      end: TODAY,
    },
    dataset: 'interactions',
  })

  const results = data ? data[0] : null

  return (
    <footer className="gap-sm bg-accent m-4 mt-0 grid grid-cols-3 rounded-md">
      <div className="border-r-background border-r-2 px-4 py-2">
        <p className="text-accent-foreground">Score</p>
        <p className="font-semibold">{toPercent(props.score)}</p>
      </div>
      <div className="border-r-background border-r-2 px-4 py-2">
        <p className="text-accent-foreground">Agent AQS</p>
        <p className="font-semibold">{toPercent(results?.agentAvg)}</p>
      </div>
      <div className="px-4 py-2">
        <p className="text-accent-foreground">Org AQS</p>
        <p className="font-semibold">{toPercent(results?.orgAvg)}</p>
      </div>
    </footer>
  )
}

interface Props extends ComponentPropsWithoutRef<typeof TooltipContent> {
  name: string
  definition: string
  scale: ScorecardScale
  notes?: Nullable<string>
  score?: Nullable<number>
  auto?: boolean
  autoFail?: boolean
  actions?: { label: ReactNode; to: string }[]
  userId?: Nullable<string>
  criteriaId?: Nullable<string>
  criteriaAssignmentId?: Nullable<string>
  evaluationId?: Nullable<string>
  evaluationScoreId?: Nullable<string>
  awardedPoints?: Nullable<number>
  autoScoreCriteria: boolean
  updatedBy?: Nullable<string>
  assignment?: {
    definition?: string | null
    suggestedVerbiage: string[]
    scoreGuidance: Record<string, Record<string, string>>
    weight: number
    autoFail: boolean
  } | null
  isSingleScorecardFiltered?: boolean
}
